import React, { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { api } from "common";

import DashboardIcon from "@mui/icons-material/Dashboard";
import ViewListIcon from "@mui/icons-material/ViewList";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CategoryIcon from "@mui/icons-material/Category";
import GroupIcon from "@mui/icons-material/Group";
import AssessmentIcon from "@mui/icons-material/Assessment";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SensorsIcon from "@mui/icons-material/Sensors";
import BusinessIcon from "@mui/icons-material/Business";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const drawerWidth = 270;

export default function ResponsiveDrawer(props) {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const { signOff } = api;
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [role, setRole] = useState(null);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const LogOut = () => {
    handleCloseUserMenu();
    dispatch(signOff());
  };

  const isSuperAdmin = auth.profile?.is_superadmin || role === "superadmin";
  const tenantName = auth.profile?.tenantName || auth.profile?.tenant_name;
  const userName = auth.profile
    ? `${auth.profile.firstName || ""} ${auth.profile.lastName || ""}`.trim() ||
      "Gestor"
    : "Gestor";
  const userInitials = userName
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const userRoleLabel = isSuperAdmin
    ? "SUPERADMIN"
    : role === "admin"
      ? "ADMIN"
      : role === "fleetadmin"
        ? "FROTA"
        : "CONDUTOR";

  // Seções de Navegação segmentadas por escopo
  const navigationSections = isSuperAdmin
    ? [
        {
          title: "PLATAFORMA & CLIENTES",
          items: [
            {
              name: "Empresas (Tenants)",
              url: "/tenants",
              icon: <BusinessIcon sx={{ fontSize: 20 }} />,
            },
            {
              name: "Gestores & Equipes",
              url: "/users",
              icon: <GroupIcon sx={{ fontSize: 20 }} />,
            },
          ],
        },
        {
          title: "SISTEMA & CONFIGURAÇÕES",
          items: [
            {
              name: "Tipos de Veículo",
              url: "/cartypes",
              icon: <CategoryIcon sx={{ fontSize: 20 }} />,
            },
            {
              name: "Notificações Globais",
              url: "/notifications",
              icon: <NotificationsNoneIcon sx={{ fontSize: 20 }} />,
            },
            {
              name: "Configurações",
              url: "/settings",
              icon: <SettingsIcon sx={{ fontSize: 20 }} />,
            },
          ],
        },
      ]
    : role === "driver"
      ? [
          {
            title: "MINHAS ROTAS",
            items: [
              {
                name: "Minhas Rotas",
                url: "/bookings",
                icon: <ViewListIcon sx={{ fontSize: 20 }} />,
              },
              {
                name: "Meu Veículo",
                url: "/cars",
                icon: <DirectionsCarIcon sx={{ fontSize: 20 }} />,
              },
            ],
          },
        ]
      : [
          // Gestor de Frota / Nível Empresa (fleetadmin)
          {
            title: "OPERAÇÃO & ROTAS",
            items: [
              {
                name: "Torre de Controle",
                url: "/dashboard",
                icon: <DashboardIcon sx={{ fontSize: 20 }} />,
              },
              {
                name: "Controle de Rotas",
                url: "/bookings",
                icon: <ViewListIcon sx={{ fontSize: 20 }} />,
              },
              {
                name: "Despachar Rota",
                url: "/addbookings",
                icon: <AltRouteIcon sx={{ fontSize: 20 }} />,
              },
            ],
          },
          {
            title: "FROTA & EQUIPE",
            items: [
              {
                name: "Frota de Veículos",
                url: "/cars",
                icon: <DirectionsCarIcon sx={{ fontSize: 20 }} />,
              },
              {
                name: "Motoristas & Equipe",
                url: "/users",
                icon: <GroupIcon sx={{ fontSize: 20 }} />,
              },
            ],
          },
          {
            title: "RELATÓRIOS",
            items: [
              {
                name: "Relatórios da Frota",
                url: "/allreports",
                icon: <AssessmentIcon sx={{ fontSize: 20 }} />,
              },
            ],
          },
        ];

  // Identificar página atual para o título da Topbar
  const getCurrentPageTitle = () => {
    for (let section of navigationSections) {
      for (let item of section.items) {
        if (location.pathname === item.url) {
          return item.name;
        }
      }
    }
    if (location.pathname === "/profile") return "Meu Perfil";
    if (location.pathname === "/tenants") return "Empresas (Tenants)";
    return isSuperAdmin ? "Empresas (Tenants)" : "Torre de Controle";
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#0f172a",
        color: "#f8fafc",
        userSelect: "none",
      }}
    >
      {/* Brand Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2.8,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Avatar
          sx={{
            bgcolor: "#2563eb",
            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
            width: 40,
            height: 40,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.4)",
          }}
        >
          <AltRouteIcon sx={{ color: "#ffffff", fontSize: 22 }} />
        </Avatar>
        <Box sx={{ overflow: "hidden" }}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{
              fontWeight: 800,
              color: "#f8fafc",
              lineHeight: 1.2,
              letterSpacing: "-0.3px",
              maxWidth: 170,
            }}
          >
            {tenantName || "Alpha Giro"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#38bdf8",
              fontWeight: 700,
              letterSpacing: "0.8px",
              fontSize: "9px",
              textTransform: "uppercase",
            }}
          >
            {isSuperAdmin ? "SUPERADMIN • ALPHA GIRO" : "Controle de Frota"}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Sections */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          py: 1.5,
          px: 1.2,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(255,255,255,0.1)",
            borderRadius: "4px",
          },
        }}
      >
        {role
          ? navigationSections.map((section, sIdx) => {
              const visibleItems = section.items;
              if (!visibleItems || visibleItems.length === 0) return null;

              return (
                <Box key={sIdx} sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      py: 0.8,
                      display: "block",
                      color: "#64748b",
                      fontWeight: 700,
                      fontSize: "10px",
                      letterSpacing: "1px",
                    }}
                  >
                    {section.title}
                  </Typography>
                  <List disablePadding dense>
                    {visibleItems.map((item, iIdx) => {
                      const isActive = location.pathname === item.url;
                      return (
                        <ListItem key={iIdx} disablePadding sx={{ mb: 0.4 }}>
                          <ListItemButton
                            component={Link}
                            to={item.url}
                            onClick={() => mobileOpen && setMobileOpen(false)}
                            sx={{
                              borderRadius: "10px",
                              py: 1,
                              px: 1.8,
                              transition: "all 0.15s ease",
                              bgcolor: isActive
                                ? "rgba(37, 99, 235, 0.16)"
                                : "transparent",
                              color: isActive ? "#38bdf8" : "#94a3b8",
                              borderLeft: isActive
                                ? "3px solid #38bdf8"
                                : "3px solid transparent",
                              "&:hover": {
                                bgcolor: isActive
                                  ? "rgba(37, 99, 235, 0.22)"
                                  : "rgba(255, 255, 255, 0.05)",
                                color: isActive ? "#38bdf8" : "#f1f5f9",
                                "& .MuiListItemIcon-root": {
                                  color: isActive ? "#38bdf8" : "#f1f5f9",
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 34,
                                color: isActive ? "#38bdf8" : "#64748b",
                                transition: "color 0.15s ease",
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontSize: "13px",
                                fontWeight: isActive ? 700 : 500,
                                letterSpacing: "-0.1px",
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              );
            })
          : null}
      </Box>

      {/* User Profile Footer */}
      <Box
        sx={{
          p: 1.8,
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
          bgcolor: "rgba(0, 0, 0, 0.2)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "rgba(255, 255, 255, 0.04)",
            borderRadius: "12px",
            p: 1.2,
            border: "1px solid rgba(255, 255, 255, 0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              overflow: "hidden",
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: "13px",
                fontWeight: 700,
                bgcolor: "#1e293b",
                color: "#38bdf8",
                border: "1.5px solid #3b82f6",
              }}
            >
              {userInitials}
            </Avatar>
            <Box sx={{ overflow: "hidden" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "#f8fafc",
                  fontSize: "12.5px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.2,
                }}
              >
                {userName}
              </Typography>
              <Chip
                label={userRoleLabel}
                size="small"
                sx={{
                  height: 16,
                  fontSize: "9px",
                  fontWeight: 800,
                  bgcolor: "rgba(56, 189, 248, 0.15)",
                  color: "#38bdf8",
                  px: 0.2,
                  mt: 0.3,
                }}
              />
            </Box>
          </Box>
          <Tooltip title="Sair">
            <IconButton
              onClick={LogOut}
              size="small"
              sx={{
                color: "#94a3b8",
                "&:hover": {
                  color: "#ef4444",
                  bgcolor: "rgba(239, 68, 68, 0.12)",
                },
              }}
            >
              <ExitToAppIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <CssBaseline />

      {/* Modern Clean Topbar (AppBar) */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: "rgba(255, 255, 255, 0.88)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid #e2e8f0",
          color: "#0f172a",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "64px",
            px: { xs: 2, sm: 3 },
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {/* Lado Esquerdo: Hamburger Mobile + Breadcrumb/Título */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 0.5, display: { sm: "none" }, color: "#334155" }}
            >
              <MenuIcon />
            </IconButton>

            <Box>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 800,
                  color: "#0f172a",
                  fontSize: { xs: "16px", sm: "18px" },
                  letterSpacing: "-0.3px",
                }}
              >
                {getCurrentPageTitle()}
              </Typography>
            </Box>

            <Chip
              icon={
                <SensorsIcon
                  sx={{
                    fontSize: "13px !important",
                    color: "#16a34a !important",
                  }}
                />
              }
              label="ONLINE"
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 800,
                bgcolor: "#dcfce7",
                color: "#15803d",
                border: "1px solid #bbf7d0",
                display: { xs: "none", md: "inline-flex" },
              }}
            />

            {tenantName && (
              <Chip
                icon={
                  <BusinessIcon
                    sx={{
                      fontSize: "13px !important",
                      color: "#2563eb !important",
                    }}
                  />
                }
                label={tenantName}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: "#eff6ff",
                  color: "#1d4ed8",
                  border: "1px solid #bfdbfe",
                  display: { xs: "none", lg: "inline-flex" },
                }}
              />
            )}
          </Box>

          {/* Lado Direito: Ações Rápidas + Notificações + Menu de Usuário */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* Botão de Atalho Rápido para Despachar */}
            {role && (role === "admin" || role === "fleetadmin") && (
              <Button
                component={Link}
                to="/addbookings"
                variant="contained"
                size="small"
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: "#2563eb",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "12.5px",
                  borderRadius: "9px",
                  px: 1.8,
                  py: 0.7,
                  boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
                  display: { xs: "none", sm: "inline-flex" },
                  "&:hover": { bgcolor: "#1d4ed8" },
                }}
              >
                Nova Rota
              </Button>
            )}

            {/* Ícone de Notificações */}
            <Tooltip title="Notificações">
              <IconButton
                component={Link}
                to="/notifications"
                sx={{
                  color: "#64748b",
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  "&:hover": { bgcolor: "#f8fafc", color: "#0f172a" },
                }}
              >
                <Badge color="error" variant="dot">
                  <NotificationsNoneIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Menu Dropdown de Usuário */}
            <Box>
              <Button
                onClick={handleOpenUserMenu}
                sx={{
                  p: 0.5,
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: "12px",
                    fontWeight: 800,
                    bgcolor: "#eff6ff",
                    color: "#2563eb",
                  }}
                >
                  {userInitials}
                </Avatar>
                <Box
                  sx={{
                    textAlign: "left",
                    display: { xs: "none", md: "block" },
                    pr: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#0f172a",
                      lineHeight: 1.1,
                    }}
                  >
                    {userName}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "10px", color: "#64748b", fontWeight: 500 }}
                  >
                    {userRoleLabel}
                  </Typography>
                </Box>
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 16, color: "#64748b" }}
                />
              </Button>

              <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                PaperProps={{
                  elevation: 4,
                  sx: {
                    mt: 1.5,
                    minWidth: 190,
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.08)",
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem
                  component={Link}
                  to="/profile"
                  onClick={handleCloseUserMenu}
                  sx={{ py: 1.2, gap: 1.2, fontSize: "13px", fontWeight: 600 }}
                >
                  <AccountCircleIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  Meu Perfil
                </MenuItem>
                {role === "admin" && (
                  <MenuItem
                    component={Link}
                    to="/settings"
                    onClick={handleCloseUserMenu}
                    sx={{
                      py: 1.2,
                      gap: 1.2,
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    Configurações
                  </MenuItem>
                )}
                <MenuItem
                  onClick={LogOut}
                  sx={{
                    py: 1.2,
                    gap: 1.2,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#ef4444",
                  }}
                >
                  <ExitToAppIcon sx={{ fontSize: 18, color: "#ef4444" }} />
                  Sair do Sistema
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Navigation (Drawer) */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="fleet navigation"
      >
        <Drawer
          variant="temporary"
          anchor={isRTL === "rtl" ? "right" : "left"}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              border: "none",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === "rtl" ? "right" : "left"}
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              border: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Toolbar sx={{ minHeight: "64px" }} />
        <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>{props.children}</Box>
      </Box>
    </Box>
  );
}
